import React, { useEffect, useRef } from 'react';
import { useLedgerGrid } from './LedgerGrid';
import LedgerCellEditor from './LedgerCellEditor';

const DIRECTIONS = {
	ArrowUp: 'up',
	ArrowDown: 'down',
	ArrowLeft: 'left',
	ArrowRight: 'right',
	PageUp: 'pageUp',
	PageDown: 'pageDown',
};

function typingStartsAnEdit(event) {
	return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
}

/**
 * What a click's modifier keys mean, in the terms `LedgerGrid.select()` takes.
 *
 * `metaKey` alongside `ctrlKey` because on a mac ctrl-click is the context menu and command-click
 * is what adds to a selection; taking only one of the two makes the feature unreachable on one
 * platform or the other.
 *
 * Ctrl and shift together extends the newest range, which is the same thing shift alone does --
 * the ranges before it are untouched either way, so there is nothing for the ctrl to add.
 */
function selectionMode(event) {
	if(event.shiftKey) {
		return 'extend';
	}
	return (event.ctrlKey || event.metaKey) ? 'add' : 'replace';
}

/**
 * One cell of the ledger: selectable always, editable sometimes.
 *
 * Every cell is selectable, including the ones nothing can be typed into -- a category name, a
 * column heading, a computed Remain. That is not a technicality. Reading a spreadsheet is done
 * with the arrow keys as much as writing one is, and a grid whose cursor skips everything
 * read-only lurches past whole regions and cannot be used to read down a column at all.
 *
 * The selected cell is a genuinely focused DOM element, so keys arrive here rather than at some
 * container that would have to work out which cell they were meant for, and so a screen reader is
 * told what moved. `tabIndex` is roving: exactly one cell in the grid is tabbable at a time, which
 * is what keeps a Tab from outside landing on the selection rather than on row one of the budget.
 *
 * A cell can also be part of a wider selection without being the focused one -- shift for a block,
 * ctrl for a scattering, or a drag for either. That is what the status bar sums, and it is why the
 * mouse handlers here are more than a click: a drag arrives as a mouseenter with the button still
 * down, on a cell that is not the one the drag started on.
 *
 * @param edit {Object|null} `{ type, value, commit, clear }` -- absent on a read-only cell
 */
export default function LedgerCell({ paneIndex, row, cellIndex, tag = 'td', className = '', style, title, edit, children }) {
	const { selection, editing, engaged, select, selected: inSelection, move, beginDrag, dragTo, beginEdit, endEdit } = useLedgerGrid();
	const cellRef = useRef(null);

	const cell = row.cells[cellIndex];
	const selected = !!selection
		&& selection.paneIndex === paneIndex
		&& selection.rowIndex === row.index
		&& selection.cellIndex === cellIndex;
	const editable = !!cell.editable && !!edit;
	const editingHere = selected && !!editing && editable;

	useEffect(() => {
		if(engaged && selected && !editingHere && cellRef.current && document.activeElement !== cellRef.current) {
			cellRef.current.focus();
		}
	}, [engaged, selected, editingHere]);

	function onMouseDown(event) {
		if(editingHere) {
			// A click inside the open editor is the caret being placed, not a cell being picked.
			return;
		}
		// Re-clicking the cell the cursor is already on is left alone: it is how a click lands on a
		// cell about to be double-clicked, and re-selecting it would collapse a selection that
		// this cell is merely the corner of.
		const mode = selectionMode(event);
		if(mode !== 'replace' || !selected) {
			// Shift-clicking across cells otherwise drags the browser's own text selection over
			// the block, which leaves the sheet looking highlighted in two different ways at once.
			event.preventDefault();
			select(paneIndex, row.index, cellIndex, mode);
		}
		beginDrag();
	}

	function onMouseEnter(event) {
		// `buttons` rather than a click count: the drag may have started on a cell in the other
		// table, and this is the only thing that says the button is still down as it arrives here.
		if(event.buttons === 1) {
			dragTo(paneIndex, row.index, cellIndex);
		}
	}

	function onDoubleClick() {
		if(editable) {
			select(paneIndex, row.index, cellIndex);
			beginEdit();
		}
	}

	function onKeyDown(event) {
		if(editingHere) {
			// The editor owns its keys; this handler would otherwise see every keystroke typed
			// into the input bubble up out of it.
			return;
		}
		const direction = DIRECTIONS[event.key];
		if(direction) {
			event.preventDefault();
			move(direction, event.shiftKey);
			return;
		}
		if(event.key === 'Tab') {
			event.preventDefault();
			move(event.shiftKey ? 'left' : 'right');
			return;
		}
		if(event.key === 'Home' || event.key === 'End') {
			event.preventDefault();
			const ends = { Home: event.ctrlKey ? 'gridStart' : 'rowStart', End: event.ctrlKey ? 'gridEnd' : 'rowEnd' };
			move(ends[event.key], event.shiftKey);
			return;
		}
		if(event.key === 'Enter' || event.key === 'F2') {
			event.preventDefault();
			if(editable) {
				beginEdit();
			} else if(event.key === 'Enter') {
				move('down');
			}
			return;
		}
		if(event.key === 'Delete' || event.key === 'Backspace') {
			event.preventDefault();
			if(editable && edit.clear) {
				edit.clear();
			}
			return;
		}
		if(editable && typingStartsAnEdit(event)) {
			event.preventDefault();
			beginEdit(event.key);
		}
	}

	const Tag = tag;
	const classes = [
		className,
		selected ? 'selected' : '',
		// Both can be true at once: the cursor sits inside the range it anchors, and the two are
		// drawn differently -- a box around the one, a wash over the rest.
		inSelection(paneIndex, row, cellIndex) ? 'in_selection' : '',
		// The category cell clips its text so a long name cannot widen the column; while it is
		// being edited the suggestion list hangs out of it and must not be clipped with it.
		editingHere ? 'editing' : '',
		editable ? 'editable' : 'read_only',
	].filter(Boolean).join(' ');

	return (
		<Tag
			ref={cellRef}
			className={classes}
			style={style}
			title={title}
			colSpan={cell.span || undefined}
			tabIndex={selected ? 0 : -1}
			onMouseDown={onMouseDown}
			onMouseEnter={onMouseEnter}
			onDoubleClick={onDoubleClick}
			onKeyDown={onKeyDown}
		>
			{editingHere &&
			<LedgerCellEditor
				type={edit.type}
				value={edit.value}
				initial={editing.initial}
				selectedDates={edit.selectedDates}
				onCommit={(value, direction) => {
					edit.commit(value);
					endEdit(direction);
				}}
				onCancel={(direction) => endEdit(direction || null)}
			/>
			}
			{!editingHere && children}
		</Tag>
	);
}
